import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowUpDown } from "lucide-react";

interface StateProgressData {
  state: string;
  discovered: number;
  completed: number;
  total_hospitals: number;
  pct_with_url: number;
  pct_complete: number;
  with_url: number;
  without_url: number;
}

type SortField = 'state' | 'discovered' | 'completed' | 'pct_complete' | 'pct_with_url' | 'total_hospitals';
type SortDirection = 'asc' | 'desc';

export const AllStatesProgressPanel = () => {
  const [sortField, setSortField] = useState<SortField>('discovered');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const { data: statesProgress, isLoading } = useQuery({
    queryKey: ['all-states-progress'],
    queryFn: async () => {
      const { data: hospitals, error } = await supabase
        .from('hospitals')
        .select('state, import_status, chargemaster_url');

      if (error) throw error;

      // Aggregate by state
      const stateMap = new Map<string, StateProgressData>();

      hospitals?.forEach(hospital => {
        const state = hospital.state;
        if (!state || state === 'ST') return; // Skip invalid/test states

        if (!stateMap.has(state)) {
          stateMap.set(state, {
            state,
            discovered: 0,
            completed: 0,
            total_hospitals: 0,
            pct_with_url: 0,
            pct_complete: 0,
            with_url: 0,
            without_url: 0,
          });
        }

        const stateData = stateMap.get(state)!;
        stateData.total_hospitals++;

        // Count discovered/pending
        if (['discovered', 'pending'].includes(hospital.import_status)) {
          stateData.discovered++;
          if (hospital.chargemaster_url) {
            stateData.with_url++;
          } else {
            stateData.without_url++;
          }
        }

        // Count completed
        if (['completed', 'imported', 'importing'].includes(hospital.import_status)) {
          stateData.completed++;
        }
      });

      // Calculate percentages
      const results: StateProgressData[] = Array.from(stateMap.values()).map(state => ({
        ...state,
        pct_with_url: state.discovered > 0
          ? Math.round((state.with_url / state.discovered) * 100)
          : 0,
        pct_complete: state.total_hospitals > 0
          ? Math.round((state.completed / state.total_hospitals) * 100)
          : 0,
      }));

      return results;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedData = statesProgress?.slice().sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDirection === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    return sortDirection === 'asc'
      ? (aVal as number) - (bVal as number)
      : (bVal as number) - (aVal as number);
  });

  const SortButton = ({ field, label }: { field: SortField; label: string }) => (
    <Button
      variant="ghost"
      size="sm"
      className="h-8 px-2"
      onClick={() => handleSort(field)}
    >
      {label}
      <ArrowUpDown className="ml-2 h-3 w-3" />
    </Button>
  );

  const getCompletionBadge = (pct: number) => {
    if (pct >= 80) return <Badge variant="default">Excellent</Badge>;
    if (pct >= 50) return <Badge variant="secondary">Good</Badge>;
    if (pct >= 25) return <Badge className="bg-orange-500">In Progress</Badge>;
    return <Badge variant="destructive">Needs Work</Badge>;
  };

  const getUrlBadge = (pct: number) => {
    if (pct >= 95) return <Badge variant="default">{pct}%</Badge>;
    if (pct >= 85) return <Badge variant="secondary">{pct}%</Badge>;
    return <Badge variant="outline">{pct}%</Badge>;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Loading state progress data...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>All States Ingestion Progress</CardTitle>
        <CardDescription>
          Complete overview of hospital data ingestion across all U.S. states
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]">
                  <SortButton field="state" label="State" />
                </TableHead>
                <TableHead className="text-right">
                  <SortButton field="total_hospitals" label="Total" />
                </TableHead>
                <TableHead className="text-right">
                  <SortButton field="discovered" label="Discovered" />
                </TableHead>
                <TableHead className="text-right">
                  <SortButton field="completed" label="Completed" />
                </TableHead>
                <TableHead className="text-center">
                  <SortButton field="pct_complete" label="% Complete" />
                </TableHead>
                <TableHead className="w-[200px]">Completion Progress</TableHead>
                <TableHead className="text-center">
                  <SortButton field="pct_with_url" label="% With URL" />
                </TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData?.map((state) => (
                <TableRow key={state.state}>
                  <TableCell className="font-bold">{state.state}</TableCell>
                  <TableCell className="text-right font-mono">
                    {state.total_hospitals.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {state.discovered.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {state.completed.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-center font-mono">
                    <span className={
                      state.pct_complete >= 50 ? 'text-green-600 font-semibold' :
                      state.pct_complete >= 25 ? 'text-yellow-600' :
                      'text-red-600'
                    }>
                      {state.pct_complete}%
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <Progress value={state.pct_complete} className="h-2" />
                      <div className="text-xs text-muted-foreground">
                        {state.completed} / {state.total_hospitals}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {getUrlBadge(state.pct_with_url)}
                  </TableCell>
                  <TableCell>
                    {getCompletionBadge(state.pct_complete)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {sortedData && (
          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <div>
              Total States: {sortedData.length}
            </div>
            <div>
              Total Hospitals: {sortedData.reduce((sum, s) => sum + s.total_hospitals, 0).toLocaleString()}
            </div>
            <div>
              Total Completed: {sortedData.reduce((sum, s) => sum + s.completed, 0).toLocaleString()}
            </div>
            <div>
              Overall: {Math.round(
                (sortedData.reduce((sum, s) => sum + s.completed, 0) /
                 sortedData.reduce((sum, s) => sum + s.total_hospitals, 0)) * 100
              )}%
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
